import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  User,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  Auth,
  UserInfo,
} from "firebase/auth";
import { auth, initializeFirebase } from "../lib/firebase";
import { useNavigate } from "react-router-dom";
import { api } from "../api/baseApi";
import { queryClient } from "../providers/QueryProvider";
import { storeRegistry } from "../stores/storeRegistry";
import { ROLES, toRole } from "@/common/permissions";
import { FEATURE_FLAGS, FeatureFlags } from "@/common/featureFlags";
import { getLoginRedirectFromLocation } from "@/common/routes.common";
import { captureException, setUserContext } from "../utils/sentry";
import { getMerchantLocations, getActiveLocations, getCurrentMerchantUser } from "@/api/merchant.api";
import { useActiveLocationsStore } from "@/stores/useActiveLocationsStore";
import { connectHubPreview, getHubPreviewToken, IS_HUB_PREVIEW } from "@/preview/hubPreview";
import { applyHubPreviewHeaders } from "@/preview/hubPreviewApi";
import { HUB_PREVIEW_TOKEN_HEADER } from "@/preview/hubPreviewProtocol";

export type UserType = User &
  UserInfo & {
    role: ROLES;
    hasDashboardAccess: boolean;
    featureFlags: FeatureFlags;
  };

interface MerchantUserAccess {
  hasDashboardAccess: boolean;
  featureFlags: FeatureFlags;
}

// Fails closed: if `/merchant/me` errors, the dashboard and every flag are off.
const fetchMerchantUserAccess = async (): Promise<MerchantUserAccess> => {
  try {
    const response = await getCurrentMerchantUser();
    return {
      hasDashboardAccess: response.data.hasDashboardAccess,
      featureFlags: response.data.featureFlags ?? {},
    };
  } catch (error) {
    captureException(error as Error, {
      extra: {
        component: 'AuthContext',
        action: 'fetchMerchantUserAccess',
      },
    });
    return { hasDashboardAccess: false, featureFlags: {} };
  }
};

interface AuthContextType {
  user: UserType | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithOtp: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const USER_READY_TIMEOUT_MS = 10_000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const HubPreviewUnavailable = () => (
  <div className="flex min-h-screen items-center justify-center p-6 text-center text-muted-foreground">
    The merchant dashboard preview could not be loaded.
  </div>
);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInstance, setAuthInstance] = useState<Auth | null>(null);
  const navigate = useNavigate();
  // Resolved by the auth observer once the signed-in user is fully set, so sign-in only navigates
  // to the home redirect after `user` (including dashboard access) is available.
  const userReadyResolverRef = useRef<(() => void) | null>(null);

  // Capped so sign-in still navigates if the observer never reports back (e.g. re-signing in as the same user).
  const waitForAuthenticatedUser = () =>
    new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        userReadyResolverRef.current = null;
        resolve();
      }, USER_READY_TIMEOUT_MS);
      userReadyResolverRef.current = () => {
        clearTimeout(timeout);
        resolve();
      };
    });

  const resolveAuthenticatedUser = () => {
    userReadyResolverRef.current?.();
    userReadyResolverRef.current = null;
  };

  const setupTokenGetter = (authInst: Auth | null) => {
    let tokenRefreshPromise: Promise<string | null> | null = null;
    
    api.setTokenGetter(async (forceRefresh: boolean) => {
      const currentUser = authInst?.currentUser;
      if (!currentUser) return null;

      if (tokenRefreshPromise) {
        return tokenRefreshPromise;
      }

      const MAX_RETRIES = 3;
      const RETRY_DELAYS = [500, 1000, 2000];
      
      const isIndexedDBError = (error: any): boolean => {
        const errorMessage = error?.message?.toLowerCase() || '';
        return (
          errorMessage.includes('indexeddb') ||
          errorMessage.includes('indexed database') ||
          errorMessage.includes('idbdatabase') ||
          errorMessage.includes('connection to indexed database server lost') ||
          error?.code === 'auth/internal-error'
        );
      };

      tokenRefreshPromise = (async () => {
        try {
          for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const token = await currentUser.getIdToken(forceRefresh || attempt > 0);
          
          if (attempt > 0) {
            console.info(`Token retrieved successfully after ${attempt} retries`);
          }
          
          return token;
        } catch (error) {
          const isLastAttempt = attempt === MAX_RETRIES;
          const isDBError = isIndexedDBError(error);
          
          console.error(
            `Failed to get user token (attempt ${attempt + 1}/${MAX_RETRIES + 1}):`,
            error
          );

          captureException(error as Error, {
            extra: {
              component: 'AuthContext',
              action: 'setupTokenGetter',
              attempt: attempt + 1,
              maxRetries: MAX_RETRIES + 1,
              forceRefresh,
              isIndexedDBError: isDBError,
              errorCode: (error as any)?.code,
              isLastAttempt,
            },
          });

          if (isDBError && !isLastAttempt) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
            continue;
          }

          if (isLastAttempt) {
            console.error('All token retrieval attempts failed');
          }
          
          return null;
        }
      }

        return null;
        } finally {
          tokenRefreshPromise = null;
        }
      })();

      return tokenRefreshPromise;
    });
  };

  const setAuthenticatedUser = async (currentUser: User) => {
    // The home redirect depends on dashboard access and feature flags, so they must be known before the user is set.
    const [userClaim, access] = await Promise.all([
      currentUser?.getIdTokenResult(),
      fetchMerchantUserAccess(),
    ]);
    const role = userClaim?.claims.merchant_user_role as ROLES;

    const userWithRole = { ...currentUser, role, ...access } as UserType;

    setUser(userWithRole);

    setUserContext({
      id: currentUser.uid,
      email: currentUser.email || undefined,
      role,
    });

    try {
      const [merchantLocationsResponse, activeLocationsResponse] = await Promise.all([
        getMerchantLocations(),
        getActiveLocations(),
      ]);
      
      const { setActiveLocations } = useActiveLocationsStore.getState();
      
      const locationInfos = merchantLocationsResponse.data.locations.map((loc) => ({
        locationId: loc.locationId,
        name: loc.name,
        currency: loc.currency,
      }));
      
      setActiveLocations(
        activeLocationsResponse.data.activeLocationIds,
        locationInfos
      );
    } catch (error) {
      console.error("Failed to load locations:", error);
      captureException(error as Error, {
        extra: {
          component: 'AuthContext',
          action: 'setAuthenticatedUser_loadLocations',
        },
      });
    }
  };

  // Inside the Hub's preview there is no Firebase sign-in: the Hub hands over a short-lived preview token for the
  // merchant and the backend answers as a read-only super admin of it.
  const startHubPreview = async () => {
    try {
      await connectHubPreview();
      api.setTokenGetter(getHubPreviewToken);
      api.setTokenHeader(HUB_PREVIEW_TOKEN_HEADER);
      applyHubPreviewHeaders();
      api.setReadOnly(true);

      const [me, merchantLocations] = await Promise.all([getCurrentMerchantUser(), getMerchantLocations()]);
      setUser({
        uid: me.data.userId,
        email: me.data.email,
        displayName: `${me.data.firstName} ${me.data.lastName}`.trim(),
        // `/merchant/me` sends the role in camelCase; role checks compare against the PascalCase ROLES values.
        role: toRole(me.data.role),
        hasDashboardAccess: true,
        // The Hub previews the dashboard before it is rolled out to the merchant.
        featureFlags: { ...me.data.featureFlags, [FEATURE_FLAGS.MerchantDashboard]: true },
      } as unknown as UserType);

      useActiveLocationsStore.getState().setActiveLocations(
        [],
        merchantLocations.data.locations.map((loc) => ({
          locationId: loc.locationId,
          name: loc.name,
          currency: loc.currency,
        }))
      );
    } catch (error) {
      captureException(error as Error, { extra: { component: "AuthContext", action: "startHubPreview" } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (IS_HUB_PREVIEW) {
      startHubPreview();
      return;
    }

    const initialize = async () => {
      try {
        await initializeFirebase();
        const authInst = auth();
        setAuthInstance(authInst);

        setupTokenGetter(authInst);

        const unsubscribe = onAuthStateChanged(
          authInst,
          async (currentUser) => {
            if (currentUser) {
              try {
                await setAuthenticatedUser(currentUser);
              } finally {
                resolveAuthenticatedUser();
              }
            } else {
              setUser(null);
              queryClient.clear();
              storeRegistry.resetAll();
              setUserContext(null);
              resolveAuthenticatedUser();
            }

            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.error("Failed to initialize Firebase:", error);
        captureException(error as Error, {
          extra: {
            component: 'AuthContext',
            action: 'initialize',
          },
        });
        setLoading(false);
      }
    };

    initialize();
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!authInstance) {
      throw new Error("Authentication not initialized");
    }
    const userReady = waitForAuthenticatedUser();
    try {
      await signInWithEmailAndPassword(authInstance, email, password);
    } catch (error) {
      userReadyResolverRef.current = null;
      throw error;
    }
    await userReady;
    navigate(getLoginRedirectFromLocation() ?? "/", { replace: true });
  };

  const signInWithOtp = async (token: string) => {
    if (!authInstance) {
      throw new Error("Authentication not initialized");
    }
    const userReady = waitForAuthenticatedUser();
    try {
      await signInWithCustomToken(authInstance, token);
    } catch (error) {
      userReadyResolverRef.current = null;
      throw error;
    }
    await userReady;
    navigate(getLoginRedirectFromLocation() ?? "/", { replace: true });
  };

  const signOut = async () => {
    if (IS_HUB_PREVIEW) return;
    if (!authInstance) {
      throw new Error("Authentication not initialized");
    }

    setUser(null);
    queryClient.clear();
    storeRegistry.resetAll();
    setUserContext(null);

    await firebaseSignOut(authInstance);

    navigate("/login");
  };

  const value = {
    user,
    loading,
    signIn,
    signInWithOtp,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && (IS_HUB_PREVIEW && !user ? <HubPreviewUnavailable /> : children)}
    </AuthContext.Provider>
  );
};
